import React, { useState, useEffect } from 'react'
import {
  Modal,
  Table,
  Input,
  Tag,
  Select,
  Button,
  Space,
  Image,
  Typography,
  Divider,
  Tooltip,
} from 'antd'
import type { ColumnType } from 'antd/es/table'
import styled from 'styled-components'
import { t } from '@/i18n'
import { IconFont, TagDetail } from '@/components'
import { useGetTaskDetailCaseList, useGetTaskDetailCaseAttackType, useGetCaseDetail } from './utils'
import { useDebounce, useDebounceFn } from 'ahooks'
import moment from 'moment'
import { FilterDropdownProps } from 'antd/lib/table/interface'
import leakSeverity from '@/assets/imges/leak-severity.png'
import leakCenter from '@/assets/imges/leak-center.png'
import leakLow from '@/assets/imges/leak-low.png'
import leakHigh from '@/assets/imges/leak-high.png'

interface CaseModalProps {
  open: boolean
  onClose: () => void
  taskId: string
  templateId: string
}

interface CaseInfo {
  resourceId: string
  name: string
  riskLevel:
    | 'RiskLevelUnknown'
    | 'RiskLevelLow'
    | 'RiskLevelMiddle'
    | 'RiskLevelHigh'
    | 'RiskLevelGross'
  attackTypeName: string
  updateTime: string
  executionSceneId: number
}

interface CaseResponse {
  list: CaseInfo[]
  total: number
}

// 添加展开行的状态管理
interface ExpandedRowState {
  [key: string]: boolean
}

const RiskLevelMap = {
  risk_level_low: { label: t('低危'), class: 'leak-low', icon: <Image src={leakLow} width={19} /> },
  risk_level_medium: {
    label: t('中危'),
    class: 'leak-center',
    icon: <Image src={leakCenter} width={19} />,
  },
  risk_level_high: {
    label: t('高危'),
    class: 'leak-high',
    icon: <Image src={leakHigh} width={19} />,
  },
  risk_level_critical: {
    label: t('严重'),
    class: 'leak-severity',
    icon: <Image src={leakSeverity} width={19} />,
  },
}

const resourceLevelMap = {
  ResourceLevelJunior: { label: t('初级'), class: 'case-level-low' },
  ResourceLevelMiddle: { label: t('中级'), class: 'case-level-center' },
  ResourceLevelSenior: { label: t('高级'), class: 'case-level-high' },
}

const killChainMap = {
  1: t('信息侦查'),
  2: t('武器构建'),
  3: t('载荷投递'),
  4: t('漏洞利用'),
  5: t('后门植入'),
  6: t('命令控制'),
  7: t('采取行动'),
}

const CaseModal: React.FC<CaseModalProps> = ({ open, onClose, taskId, templateId }) => {
  const [searchValue, setSearchValue] = useState('')
  // 使用 useDebounce 对搜索值进行防抖
  const debouncedSearchValue = useDebounce(searchValue, { wait: 500 })
  const [getTaskDetailCaseList, caseDataList, caseListLoading] = useGetTaskDetailCaseList()
  const [getAttackTypeList, attackTypeList] = useGetTaskDetailCaseAttackType()
  const [getCaseDetail, caseDetail, caseDetailLoading] = useGetCaseDetail()
  const [caseData, setCaseData] = useState<CaseResponse>({
    list: [],
    total: 0,
  })
  const [resourceLevelSelectOpen, setResourceLevelSelectOpen] = useState(false)
  // 添加展开行的状态
  const [expandedRows, setExpandedRows] = useState<ExpandedRowState>({})
  // 添加危险等级和攻击类型下拉状态管理
  const [riskLevelSelectOpen, setRiskLevelSelectOpen] = useState(false)
  const [attackTypeSelectOpen, setAttackTypeSelectOpen] = useState(false)
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 10,
    resourceName: undefined as string | undefined,
    resourceLevel: undefined as string | undefined,
    riskLevel: undefined as string | undefined,
    attackTypeId: undefined as string | undefined,
  })
  const [tableKey, setTableKey] = useState(0) // 新增：用于强制刷新 Table
  console.log('open', open, taskId, templateId)

  // 重置所有状态的函数
  const resetState = () => {
    setSearchValue('')
    setSearchParams({
      page: 1,
      pageSize: 10,
      resourceName: undefined,
      riskLevel: undefined,
      attackTypeId: undefined,
      resourceLevel: undefined,
    })
    setExpandedRows({})
    setCaseData({
      list: [],
      total: 0,
    })
  }

  // 处理关闭弹窗
  const handleClose = () => {
    resetState()
    setTableKey(prev => prev + 1) // 新增：强制 Table 重新挂载
    setSearchParams(prev => ({ ...prev, page: 1 }))
    onClose()
  }

  // 处理搜索输入
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value) // 立即更新输入框的值
  }

  // 使用 useDebounce 处理防抖后的值

  useEffect(() => {
    setSearchParams(prev => ({ ...prev, page: 1, resourceName: debouncedSearchValue }))
  }, [debouncedSearchValue])

  useEffect(() => {
    if (open && taskId && templateId) {
      getTaskDetailCaseList({
        taskId,
        templateId,
        ...searchParams,
      })
        .then(res => {
          setCaseData({ list: res.data.list, total: res.data.total })
          // 保持展开行的状态
          const currentExpandedRows = { ...expandedRows }
          setExpandedRows(currentExpandedRows)
        })
        .catch(_ => {})
    }
  }, [open, taskId, templateId, searchParams])

  useEffect(() => {
    if (open) {
      getAttackTypeList({ taskId, templateId }).catch(_ => {})
    }
  }, [open])

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setSearchParams(prev => ({
      ...prev,
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      riskLevel: filters.riskLevel,
      resourceLevel: filters.resourceLevel,
      attackTypeId: filters.attackTypeName,
      sortField: sorter.field
        ? sorter.field === 'updateTime'
          ? 'sort_field_update_time'
          : undefined
        : undefined,
      sortOrder:
        sorter.order === 'ascend'
          ? 'SORT_ORDER_ASCENDING'
          : sorter.order === 'descend'
          ? 'SORT_ORDER_DESCENDING'
          : undefined,
    }))
  }

  // 处理展开/收起行
  const handleExpand = async (expanded: boolean, record: CaseInfo) => {
    setExpandedRows(prev => ({ ...prev, [record.resourceId]: expanded }))
    if (expanded) {
      try {
        await getCaseDetail({ resourceId: record.resourceId, taskId })
      } catch (error) {
        // 错误已经在 hook 中处理
      }
    }
  }

  // 自定义展开行内容
  const expandedRowRender = (record: CaseInfo) => {
    if (caseDetailLoading) {
      return <div style={{ padding: '20px 0' }}>{t('加载中...')}</div>
    }

    return (
      <StyledExpandedRow>
        <div className="detail-section">
          <div className="upper-section">
            <div className="detail-grid">
              <div className="detail-item">
                <span className="label">{t('验证模式：')}</span>
                <span className="value">
                  {caseDetail?.verificationModeId === 5
                    ? t('Agent → 容器')
                    : caseDetail?.verificationModeId === 10
                    ? t('容器Agent → Listener')
                    : '-'}
                </span>
              </div>
              <div className="detail-item">
                <span className="label">{t('平台架构：')}</span>
                <span className="value">
                  {caseDetail?.supportedArchitectures?.map((v, i) => (
                    <span style={{ marginRight: '8px' }} key={i}>
                      {v == 1 ? 'X86' : 'ARM'}
                    </span>
                  ))}
                </span>
              </div>
            </div>
            <div className="divider" />
            <div className="detail-grid">
              <div className="detail-item">
                <span className="label">{t('操作系统：')}</span>
                <span className="value">
                  <span style={{ marginRight: '8px' }}>
                    {caseDetail?.supportedPlatforms?.includes('1') && 'Windows'}
                  </span>
                  <span style={{ marginRight: '8px' }}>
                    {caseDetail?.supportedPlatforms?.includes('3') && 'Linux'}
                  </span>
                  <span>{caseDetail?.supportedPlatforms?.includes('4') && 'UOS'}</span>
                </span>
              </div>
            </div>
          </div>
          <div className="full-width-section">
            {(caseDetail?.cveId ||
              caseDetail?.cnvdId?.length > 0 ||
              caseDetail?.cnnvdId?.length > 0) && (
              <div className="detail-item-vertical" style={{ margin: '16px 0' }}>
                <div className="label">{t('漏洞编号：')}</div>
                <div className="name">
                  {caseDetail?.cveId &&
                    caseDetail?.cveId?.map((key, index) => (
                      <>
                        <a href={key.url} target="_blank" rel="noopener noreferrer">
                          {key.name}
                        </a>
                        {index + 1 === caseDetail?.cveId?.length ? '' : ', '}
                      </>
                    ))}
                  {caseDetail?.cnvdId?.map((item, index) => (
                    <div key={index}>
                      <span>{item}</span>
                    </div>
                  ))}
                  {caseDetail?.cnnvdId?.map((item, index) => (
                    <div key={index}>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="full-width-section" style={{ marginBottom: '6px' }}>
            {caseDetail?.templateName && caseDetail?.templateName?.length > 0 && (
              <div className="detail-item-vertical">
                <div className="label" style={{ marginTop: '16px' }}>
                  {t('模板名称：')}
                </div>
                <div className="value-container">
                  {caseDetail?.templateName?.map((item, index) => (
                    <span
                      key={index}
                      style={{ marginBottom: '6px', marginRight: '10px', display: 'inline-block' }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="full-width-section">
            {caseDetail?.tactics && caseDetail?.tactics?.length > 0 && (
              <div className="detail-item-vertical">
                <div className="label">{t('ATT&CK：')}</div>
                <div className="value-container">
                  {caseDetail?.tactics?.map(item => (
                    <div key={item.id} style={{ marginBottom: '6px' }}>
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        {item.name}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="detail-item-vertical">
              {caseDetail?.tags && caseDetail?.tags?.length > 0 && (
                <TagDetail resourceTagList={caseDetail?.tags} />
              )}
            </div>
            {caseDetail?.describe && (
              <div className="detail-item-vertical">
                <div className="label">{t('描述：')}</div>
                <div className="value-container">
                  <div className="value">{caseDetail?.describe}</div>
                </div>
              </div>
            )}
            {caseDetail?.solution && (
              <div className="detail-item-vertical">
                <div className="label">{t('解决方案：')}</div>
                <div className="value-container">
                  <div className="value">{caseDetail?.solution}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </StyledExpandedRow>
    )
  }

  const columns: ColumnType<CaseInfo>[] = [
    {
      title: t('用例名称'),
      dataIndex: 'name',
      key: 'name',
      width: '23%',
      ellipsis: true,
    },
    {
      title: t('用例分级'),
      dataIndex: 'resourceLevel',
      key: 'resourceLevel',
      width: '13%',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            mode="multiple"
            style={{ width: '100%' }}
            placeholder={t('请选择用例分级')}
            value={props.selectedKeys as string[]}
            open={resourceLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setResourceLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                      setSearchParams(prev => ({ ...prev, resourceLevel: undefined }))
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setResourceLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('初级'), value: 'ResourceLevelJunior' },
              { label: t('中级'), value: 'ResourceLevelMiddle' },
              { label: t('高级'), value: 'ResourceLevelSenior' },
            ]}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),
      render: level => {
        const levelMap = {
          ResourceLevelSenior: t('高级'),
          ResourceLevelMiddle: t('中级'),
          ResourceLevelJunior: t('初级'),
        }
        const config = {
          [t('高级')]: { color: '#fff', bg: '#F44F4F' },
          [t('中级')]: { color: '#fff', bg: '#FFA709' },
          [t('初级')]: { color: '#fff', bg: '#6F9FFF' },
        }
        const displayLevel = levelMap[level] || t('初级')
        const style = config[displayLevel]
        return (
          <TypeTag bgColor={style.bg} color={style.color}>
            {displayLevel}
          </TypeTag>
        )
      },
    },
    {
      title: t('危险等级'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: '15%',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            mode="multiple"
            style={{ width: '100%' }}
            placeholder={t('请选择危险等级')}
            value={props.selectedKeys as string[]}
            open={riskLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setRiskLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                      setSearchParams(prev => ({ ...prev, riskLevel: undefined }))
                    }}
                  >
                    重置
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setRiskLevelSelectOpen(false)
                    }}
                  >
                    确定
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('严重'), value: 'risk_level_critical' },
              { label: t('高危'), value: 'risk_level_high' },
              { label: t('中危'), value: 'risk_level_medium' },
              { label: t('低危'), value: 'risk_level_low' },
            ]}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),
      render: (riskLevel: keyof typeof RiskLevelMap) => (
        <div>
          {RiskLevelMap[riskLevel]?.icon}
          <span className={RiskLevelMap[riskLevel]?.class}>{RiskLevelMap[riskLevel]?.label}</span>
        </div>
      ),
    },
    {
      title: t('攻击类型'),
      dataIndex: 'attackTypeName',
      key: 'attackTypeName',
      width: '15%',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            mode="multiple"
            style={{ width: '100%' }}
            placeholder={t('请选择攻击类型')}
            value={props.selectedKeys as string[]}
            open={attackTypeSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setAttackTypeSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                      setSearchParams(prev => ({ ...prev, attackTypeId: undefined }))
                    }}
                  >
                    重置
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    确定
                  </Button>
                </Space>
              </div>
            )}
            options={attackTypeList.map(type => ({
              label: type.attackTypeName,
              value: type.attackTypeId,
            }))}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: '20%',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (text: string) => (text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: t('操作'),
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <span
          className="text-action"
          onClick={() => handleExpand(!expandedRows[record.resourceId], record)}
        >
          {expandedRows[record.resourceId] ? t('收起') : t('展开')}
        </span>
      ),
    },
  ]

  return (
    <StyledModal
      title={t('用例列表')}
      open={open}
      zIndex={1001}
      onCancel={handleClose}
      width={900}
      footer={null}
    >
      <div className="search-container">
        <Input
          placeholder={t('用例名称')}
          allowClear
          value={searchValue}
          onChange={handleSearch}
          style={{ width: 260 }}
          prefix={<IconFont type="icon-sousuo1" style={{ color: '#666', fontSize: 16 }} />}
        />
      </div>
      <Table
        key={tableKey} // 新增：强制刷新 Table
        columns={columns}
        dataSource={caseData.list}
        rowKey="resourceId"
        loading={caseListLoading}
        showSorterTooltip={false}
        expandable={{
          expandedRowRender,
          expandedRowKeys: Object.keys(expandedRows).filter(key => expandedRows[key]),
          onExpand: handleExpand,
          expandIcon: () => null,
        }}
        pagination={{
          current: searchParams.page,
          pageSize: searchParams.pageSize,
          total: caseData.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: total => `${t('共')} ${total} ${t('条')}`,
        }}
        onChange={handleTableChange}
      />
    </StyledModal>
  )
}

const StyledExpandedRow = styled.div`
  padding: 6px 18px;
  background-color: #fff;

  .detail-section {
    display: flex;
    flex-direction: column;
    gap: 0px;
  }

  .upper-section {
    display: flex;
    gap: 40px;
    align-items: flex-start;

    .detail-grid {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .divider {
      width: 0;
      border-left: 1px dashed #d9d9d9;
      align-self: stretch;
      margin: 0 20px;
      background: none;
    }
  }

  .full-width-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .detail-item {
    display: flex;
    align-items: flex-start;

    .label {
      color: #86909c;
      min-width: 100px;
      margin-right: 8px;
      flex-shrink: 0;
    }

    .value {
      color: #1d2129;
      flex: 1;
    }
  }

  .detail-item-vertical {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .label {
      color: #86909c;
      font-size: 14px;
    }

    .value-container {
      background-color: #f7f8fa;
      padding: 12px 16px;
      border-radius: 4px;
      width: 100%;
    }

    .value {
      color: #1d2129;
      line-height: 1.5715;
    }
  }

  .tag-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: -4px;
  }

  .tag {
    margin: 4px;
    display: inline-block;
    padding: 0 8px;
    height: 24px;
    line-height: 22px;
    background: #f2f3f5;
    border: 1px solid #e5e6eb;
    border-radius: 4px;
    font-size: 12px;
    color: #4e5969;
  }

  .text-action {
    color: #4080ff;
    cursor: pointer;
    &:hover {
      color: #6aa1ff;
    }
  }

  .filter-title {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .filter-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .ant-select {
    .ant-select-selector {
      border-radius: 2px;
    }
  }

  .ant-btn-link {
    padding: 0 4px;
    height: 24px;
  }

  .leak-low {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #e1ebfe;
    color: #6f9fff;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-center {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffedce;
    color: #ffa709;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-high {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffe1e1;
    color: #ff4141;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-severity {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #f5d9d9;
    color: #b70000;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
`

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    position: relative;

    .ant-modal-close {
      width: 36px;
      height: 36px;
      background: #d8d6f4;
      box-shadow: -1px 4px 10px 0px #c7c7c7;
      border-radius: 6px;
      border: none;
      color: #4238c9;
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      right: -8px;
      top: -8px;
      transition: transform 0.3s ease;

      &:hover {
        transform: translate(-18px, 18px);
        box-shadow: 0 0 0 0;
      }

      .ant-modal-close-x {
        width: 100%;
        height: 100%;
        line-height: 36px;
        text-align: center;
      }
    }

    .ant-modal-header {
      height: 56px;
      background: #f2f0ff;
      border-bottom: none;
      margin: 0;
      padding: 19px 20px;
    }
  }

  .search-container {
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .ant-table-column-sorters {
    justify-content: flex-start;
    .ant-table-column-sorter {
      padding-top: 4px;
      margin-left: 5px;
    }
    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-table-filter-column {
    justify-content: flex-start;
    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-table-expanded-row-level-1 {
    .ant-table-cell {
      background-color: #fff;
      padding: 0 !important;
    }
  }

  .ant-table-wrapper {
    .ant-table {
      .ant-table-thead > tr > th {
        background: #f2f3f5;
        color: #1d2129;
        font-weight: 500;
      }
    }
  }

  .text-action {
    color: #4080ff;
    cursor: pointer;
    &:hover {
      color: #6aa1ff;
    }
  }

  .filter-title {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .filter-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .ant-select {
    .ant-select-selector {
      border-radius: 2px;
    }
  }

  .ant-btn-link {
    padding: 0 4px;
    height: 24px;
  }

  .leak-low {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #e1ebfe;
    color: #6f9fff;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-center {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffedce;
    color: #ffa709;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-high {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffe1e1;
    color: #ff4141;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-severity {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #f5d9d9;
    color: #b70000;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
`

const TypeTag = styled.span<{ bgColor: string; color: string }>`
  display: inline-block;
  height: 18px;
  padding: 0 4px;
  background: ${props => props.bgColor};
  border-radius: 4px;
  line-height: 18px;
  color: ${props => props.color};
  font-size: 12px;
  margin-right: 4px;
`

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`

export default CaseModal
